import React, { useEffect } from "react";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { KycStep } from "../components/KycStep";
import { StepFooter } from "../components/StepFooter";
import { preloadDocumentDetector } from "../document-camera/DocumentDetectionClient";
import { DocumentGuideAnimation } from "../document-camera/DocumentGuideAnimation";
import {
  DOCUMENT_TYPES,
  normalizeDocumentType,
} from "../services/documentRequirements";

// 证件正/反面拍摄前的准备页；展示操作要求，并在用户阅读时预热检测 Worker。
export function DocumentPreparationStep({
  side = "front",
  documentType,
  onBack,
  onContinue,
}) {
  const { t } = useTranslation();
  const isBack = side === "back";
  const isPassport =
    normalizeDocumentType(documentType) === DOCUMENT_TYPES.PASSPORT;
  const tips = ["original", "lighting", "corners"];

  useEffect(() => {
    preloadDocumentDetector().catch(() => {
      // 此处只做静默预加载；真正进入相机后由 DocumentCamera 负责提示并降级手动模式。
    });
  }, []);

  return (
    <KycStep
      labelledBy="kyc-document-preparation-title"
      className="kyc-preparation-step"
    >
      <div className="text-center">
        <DocumentGuideAnimation side={side} />

        <h1
          id="kyc-document-preparation-title"
          className="mt-7 text-[1.35rem] font-semibold leading-tight tracking-[-0.035em] text-slate-950 sm:text-[2rem]"
        >
          {t(
            isBack
              ? "kyc.preparation.backTitle"
              : isPassport
                ? "kyc.preparation.passportTitle"
                : "kyc.preparation.frontTitle",
          )}
        </h1>
        <p className="mx-auto mt-3 max-w-md text-xs leading-7 text-slate-600">
          {t(
            isBack
              ? "kyc.preparation.backDescription"
              : isPassport
                ? "kyc.preparation.passportDescription"
                : "kyc.preparation.frontDescription",
          )}
        </p>
      </div>

      <ul className="mx-auto mt-7 max-w-md space-y-3">
        {tips.map((tip) => (
          <li
            key={tip}
            className="flex items-center gap-3 text-left text-xs leading-6 text-slate-600"
          >
            <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-teal-50 text-teal-700 ring-1 ring-teal-100">
              <Check
                className="size-3.5"
                strokeWidth={2.5}
                aria-hidden="true"
              />
            </span>
            {t(`kyc.preparation.${tip}`)}
          </li>
        ))}
      </ul>

      <StepFooter
        onBack={onBack}
        onContinue={onContinue}
        continueLabel={t(
          isBack
            ? "kyc.preparation.captureBack"
            : isPassport
              ? "kyc.preparation.capturePassport"
              : "kyc.preparation.captureFront",
        )}
      />
    </KycStep>
  );
}
